import { supabase } from '../supabase/client';
import type { StaffActivityLogInsert } from '@/types/staff';

/**
 * Log staff activity for audit trail
 */
export const logActivity = async (data: {
  staff_user_id: string;
  action_type: string;
  entity_type?: string;
  entity_id?: string;
  details?: any;
}): Promise<void> => {
  try {
    // Get IP and user agent from browser
    const ip_address = null; // IP detection would need server-side
    const user_agent = navigator.userAgent;

    const activityData: StaffActivityLogInsert = {
      staff_user_id: data.staff_user_id,
      action_type: data.action_type,
      entity_type: data.entity_type || null,
      entity_id: data.entity_id || null,
      details: data.details || {},
      ip_address,
      user_agent,
      request_path: window.location.pathname,
      request_method: 'GET',
    };

    const { error } = await supabase
      .from('staff_activity_log')
      .insert(activityData as any) as any;

    if (error) {
      console.error('[ActivityService] Failed to log activity:', error);
      // Don't throw - activity logging should not break user flow
    }
  } catch (error) {
    console.error('[ActivityService] Unexpected error:', error);
    // Don't throw - activity logging should not break user flow
  }
};

/**
 * Get activity log for a staff user
 */
export const getStaffActivity = async (staffUserId: string, limit: number = 50) => {
  try {
    const { data, error } = await supabase
      .from('staff_activity_log')
      .select('*')
      .eq('staff_user_id', staffUserId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('[ActivityService] Failed to fetch activity:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[ActivityService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all activity logs (admin/operations only)
 */
export const getAllActivity = async (filters?: {
  action_type?: string;
  entity_type?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
}) => {
  try {
    let query = supabase
      .from('staff_activity_log')
      .select(`
        *,
        staff_user:staff_users(first_name, last_name, email)
      `)
      .order('created_at', { ascending: false });

    if (filters?.action_type) {
      query = query.eq('action_type', filters.action_type);
    }

    if (filters?.entity_type) {
      query = query.eq('entity_type', filters.entity_type);
    }

    if (filters?.date_from) {
      query = query.gte('created_at', filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte('created_at', filters.date_to);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    } else {
      query = query.limit(100);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[ActivityService] Failed to fetch all activity:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[ActivityService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get activity logs for a specific entity (customer, case, etc.)
 */
export const getEntityActivity = async (entityType: string, entityId: string) => {
  try {
    const { data, error } = await supabase
      .from('staff_activity_log')
      .select(`
        *,
        staff_user:staff_users(first_name, last_name, email, role)
      `)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[ActivityService] Failed to fetch entity activity:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[ActivityService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

export const activityService = {
  logActivity,
  getStaffActivity,
  getAllActivity,
  getEntityActivity,
};

